import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaChild, FaUserTie, FaFutbol, FaUsers, FaPhoneAlt, FaEnvelope, FaChevronDown, FaMapMarkerAlt } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import './FootballSchool.css';

const PHONE_DISPLAY = '+381 63 113 4892';
const PHONE_TEL = '+381631134892';
const EMAIL = 'info@juniorfk.com';
const MAP_URL = 'https://maps.app.goo.gl/kxhuAwESArRMpWxw9';
const MAP2_URL = 'https://www.google.com/maps/search/?api=1&query=45.28445,19.83474';

type FaqItem = { q: string; a: string };

/**
 * Sekcija "Škola fudbala" — nosi ključne reči za pretragu (škola fudbala, dečiji fudbal, Novi Sad)
 * i FAQ koji odgovara na pitanja roditelja. FAQ je duplo označen i u index.html (FAQPage JSON-LD).
 */
const FootballSchool: React.FC = () => {
  const { t, tList } = useLanguage();
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const faq = tList<FaqItem>('school.faq');

  const cards = [
    { icon: <FaChild />, title: t('school.cards.ages.title'), text: t('school.cards.ages.text') },
    { icon: <FaUserTie />, title: t('school.cards.coaches.title'), text: t('school.cards.coaches.text') },
    { icon: <FaFutbol />, title: t('school.cards.valencia.title'), text: t('school.cards.valencia.text') },
    { icon: <FaUsers />, title: t('school.cards.girls.title'), text: t('school.cards.girls.text') },
  ];

  const reveal = (delay = 0) => ({
    initial: isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay },
    viewport: { once: true },
  });

  return (
    <section id="skola-fudbala" className="school section-lg" aria-labelledby="school-title">
      <div className="container">
        <motion.div className="school__header" {...reveal()}>
          <span className="school__header-mark" aria-hidden />
          <p className="school__kicker">{t('school.kicker')} · {t('school.since')}</p>
          <h2 id="school-title" className="school__title">{t('school.title')}</h2>
        </motion.div>

        <motion.div className="school__intro" {...reveal(0.05)}>
          <p>{t('school.intro1')}</p>
          <p>{t('school.intro2')}</p>
        </motion.div>

        <motion.a className="school__banner" href="/upis/" {...reveal(0.08)}>
          <span className="school__banner-text">{t('school.banner.t')}</span>
          <span className="school__banner-btn">{t('school.banner.b')} →</span>
        </motion.a>

        <div className="school__cards">
          {cards.map((c, i) => (
            <motion.article key={c.title} className="school__card" {...reveal(0.1 + i * 0.08)}>
              <div className="school__card-icon" aria-hidden>{c.icon}</div>
              <h3 className="school__card-title">{c.title}</h3>
              <p className="school__card-text">{c.text}</p>
            </motion.article>
          ))}
        </div>

        <motion.nav className="school__more" aria-label={t('school.more')} {...reveal(0.1)}>
          <span className="school__more-label">{t('school.more')}:</span>
          <a href="/fudbal-za-decu-novi-sad/">{t('footer.links.kids')}</a>
          <a href="/fudbalski-klub-novi-sad/">{t('footer.links.club')}</a>
          <a href="/upis/">{t('footer.links.privateCoaching')}</a>
        </motion.nav>

        <motion.div className="school__faq" {...reveal(0.1)}>
          <h3 className="school__faq-title">{t('school.faqTitle')}</h3>
          <ul className="school__faq-list">
            {faq.map((item, i) => {
              const open = openFaq === i;
              return (
                <li key={item.q} className={`school__faq-item ${open ? 'is-open' : ''}`}>
                  <button
                    type="button"
                    className="school__faq-question"
                    aria-expanded={open}
                    aria-controls={`school-faq-${i}`}
                    onClick={() => setOpenFaq(open ? null : i)}
                  >
                    <span>{item.q}</span>
                    <FaChevronDown className="school__faq-chevron" aria-hidden />
                  </button>
                  <div id={`school-faq-${i}`} className="school__faq-answer" hidden={!open}>
                    <p>{item.a}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </motion.div>

        <motion.div className="school__cta" {...reveal(0.15)}>
          <h3 className="school__cta-title">{t('school.cta.title')}</h3>
          <p className="school__cta-text">{t('school.cta.text')}</p>
          <p className="school__cta-address">
            <FaMapMarkerAlt aria-hidden /> {t('school.address')}
          </p>
          <div className="school__cta-actions">
            <a className="school__cta-btn school__cta-btn--primary" href={`tel:${PHONE_TEL}`}>
              <FaPhoneAlt aria-hidden />
              <span>{t('school.cta.call')} {PHONE_DISPLAY}</span>
            </a>
            <a className="school__cta-btn" href={`mailto:${EMAIL}?subject=Probni%20trening`}>
              <FaEnvelope aria-hidden />
              <span>{t('school.cta.email')}</span>
            </a>
            <a className="school__cta-btn" href={MAP_URL} target="_blank" rel="noopener noreferrer">
              <FaMapMarkerAlt aria-hidden />
              <span>{t('school.cta.map')}</span>
            </a>
            <a className="school__cta-btn" href={MAP2_URL} target="_blank" rel="noopener noreferrer">
              <FaMapMarkerAlt aria-hidden />
              <span>{t('school.cta.map2')}</span>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FootballSchool;
